import {
  type Edge,
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import Box from '@mui/material/Box';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { Gender } from '@tyro/api';
import { Avatar, usePreferredNameLayout } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { CheckmarkIcon, CloseIcon } from '@tyro/icons';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useClassListSettings } from '../../../store/class-list-settings';
import { ContextMenu } from './context-menu';
import { useListManagerState } from './state';
import type { ListManagerState } from './state/types';

interface DraggableCardProps {
  index: number;
  groupId: ListManagerState['id'];
  student: ListManagerState['students'][number];
}

type DraggableState =
  | { type: 'idle' }
  | { type: 'preview'; container: HTMLElement; rect: DOMRect }
  | { type: 'dragging' };

const idleState: DraggableState = { type: 'idle' };
const draggingState: DraggableState = { type: 'dragging' };

export function DraggableCard({ index, student, groupId }: DraggableCardProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [draggableState, setDraggableState] =
    useState<DraggableState>(idleState);

  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

  const { selectedStudentIds } = useListManagerState();

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    return combine(
      draggable({
        element,
        getInitialData: () => ({
          type: 'student',
          index,
          draggableId: student.id,
          droppableId: groupId,
        }),
        onGenerateDragPreview: ({ location, source, nativeSetDragImage }) => {
          const rect = source.element.getBoundingClientRect();

          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({
              element,
              input: location.current.input,
            }),
            render({ container }) {
              setDraggableState({ type: 'preview', container, rect });
              return () => setDraggableState(draggingState);
            },
          });
        },
        onDragStart() {
          setDraggableState(draggingState);
        },
        onDrop() {
          setDraggableState(idleState);
        },
      }),
      dropTargetForElements({
        element,
        getIsSticky: () => true,
        getData({ input }) {
          return attachClosestEdge(
            {
              type: 'student',
              index,
              draggableId: student.id,
              droppableId: groupId,
            },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            },
          );
        },
        onDragEnter({ self, source }) {
          if (source.data.draggableId !== student.id) {
            setClosestEdge(extractClosestEdge(self.data));
          }
        },
        onDrag({ self, source }) {
          if (source.data.draggableId !== student.id) {
            setClosestEdge(extractClosestEdge(self.data));
          }
        },
        onDragLeave() {
          setClosestEdge(null);
        },
        onDrop() {
          setClosestEdge(null);
        },
      }),
    );
  }, [student.id, index, groupId]);

  return (
    <>
      <Box position="relative">
        {closestEdge && (
          <Divider
            sx={{
              position: 'absolute',
              width: '100%',
              borderColor: 'indigo.500',
              borderWidth: 1,
              flex: 1,
              ...(closestEdge === 'top'
                ? {
                    top: -4,
                  }
                : {
                    bottom: -4,
                  }),
            }}
          />
        )}
        <Card
          ref={elementRef}
          index={index}
          groupId={groupId}
          student={student}
          draggableState={draggableState}
        />
      </Box>
      {draggableState.type === 'preview' &&
        createPortal(
          <Box
            sx={{
              width: draggableState.rect.width,
              height: draggableState.rect.height,
            }}
          >
            <Card
              index={index}
              groupId={groupId}
              student={student}
              draggableState={draggableState}
              showSelectionCount={selectedStudentIds.length > 1}
            />
          </Box>,
          draggableState.container,
        )}
    </>
  );
}

type CardProps = {
  index: number;
  groupId: ListManagerState['id'];
  student: ListManagerState['students'][number];
  draggableState: DraggableState;
  showSelectionCount?: boolean;
};

interface GetCardStyleProps {
  theme: Theme;
  gender: Gender | undefined | null;
  showGender: boolean;
  isDragging: boolean;
  isCardSelected: boolean;
}

const getCardStyle = ({
  theme,
  gender,
  showGender,
  isDragging,
  isCardSelected,
}: GetCardStyleProps) => {
  // some basic styles to make the items look a bit nicer
  let backgroundColor = 'white';

  if (showGender && gender) {
    backgroundColor = gender === Gender.Male ? 'blue.200' : 'pink.200';
  }

  // change background color if dragging or selected
  if (isDragging) {
    backgroundColor = 'primary.lighter';
  } else if (isCardSelected) {
    backgroundColor = 'primary.light';
  }

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    userSelect: 'none',
    padding: 0.75,
    marginBottom: 0.75,
    borderRadius: 0.75,
    backgroundColor,
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    transitionDuration: '150ms',
    transitionProperty: 'background-color, opacity',
    position: 'relative',
    cursor: 'grab',
    ...(isDragging && { boxShadow: theme.customShadows.z24 }),
  } as const;
};

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ index, student, draggableState, groupId, showSelectionCount }, ref) => {
    const { t } = useTranslation(['classListManager']);
    const { displayName } = usePreferredNameLayout();

    const {
      enableDuplicateStudents,
      selectedStudentIds,
      deleteDuplicate,
      includeClassGroupName,
      performCardAction,
    } = useListManagerState();
    const { showGender } = useClassListSettings();

    const name = displayName(student?.person);

    const isCardSelected = selectedStudentIds.includes(student.id);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (!event.defaultPrevented && event.button === 0) {
        event.preventDefault();
        performCardAction(event, student.id);
      }
    };

    const onTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
      if (!event.defaultPrevented) {
        event.preventDefault();
        performCardAction(event, student.id);
      }
    };

    const onKeydown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        !event.defaultPrevented &&
        draggableState.type === 'dragging' &&
        event.key === 'Enter'
      ) {
        event.preventDefault();
        performCardAction(event, student.id);
      }
    };

    return (
      <>
        <Box
          ref={ref}
          sx={(theme) =>
            getCardStyle({
              theme,
              gender: student.gender,
              showGender,
              isDragging: draggableState.type === 'dragging',
              isCardSelected,
            })
          }
          onClick={onClick}
          onKeyDown={onKeydown}
          onTouchEnd={onTouchEnd}
          onContextMenu={(event) => {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
          }}
        >
          {showSelectionCount && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                top: -6,
                right: -6,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                width: 26,
                height: 26,
                color: 'white',
              }}
            >
              <Typography component="span" variant="subtitle2">
                {selectedStudentIds.length}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                my: 0.5,
                mr: 1,
                position: 'relative',
              }}
            >
              <Avatar
                src={student?.person.avatarUrl}
                name={name}
                person={student?.person}
                sx={{ width: 30, height: 30, fontSize: '0.625rem' }}
              />
              <Box
                aria-hidden
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  width: 30,
                  height: 30,
                  opacity: isCardSelected ? 1 : 0,
                  color: 'primary.main',
                  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  transitionDuration: '150ms',
                  transitionProperty: 'opacity',
                  '& svg path': {
                    strokeWidth: 3,
                  },
                }}
              >
                <CheckmarkIcon />
              </Box>
            </Box>
            <Stack>
              <Typography
                component="span"
                variant="subtitle2"
                sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}
              >
                {name}
              </Typography>
              {includeClassGroupName && student?.classGroupName && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.625rem', lineHeight: 1.5 }}
                >
                  {student?.classGroupName}
                </Typography>
              )}
            </Stack>
          </Box>
          {student.isDuplicate && (
            <Tooltip title={t('classListManager:removeDuplicateStudent')}>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  deleteDuplicate(groupId, student.id);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <ContextMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          studentId={student.id}
          studentIndex={index}
          groupId={groupId}
          enableDuplicateStudents={enableDuplicateStudents}
        />
      </>
    );
  },
);
